import useTranslator from '@hooks/use-translator';
import { Input } from '@components/common/input';
import { Button } from '@components/common/button';
import { PlusCircle } from 'lucide-react';
import axios from 'axios';
import {
  NEIGHBORHOOD_DICTIONARY_ENDPOINT,
  SERVER_URL,
} from '@pma/utils/api-routes';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from '@stores/auth-store';
import { parseCookies } from 'nookies';
import { ACCESS_TOKEN } from '@pma/utils/constants';
import { useState } from 'react';
import { DictType } from '@pma/types';
import { toast } from '@hooks/use-toast';
import { cn } from '@pma/utils/helpers';

interface Props {
  refetch?: CallableFunction;
}
const AddNeighborhoodForm = ({ refetch }: Props) => {
  const [name, setName] = useState('');
  const { translate, locale } = useTranslator();
  const { authConfig } = useAuth();

  const isHebrew = locale === 'he';

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const orgId = authConfig?.last_organization_id;

  const addNeighborhood = async () => {
    const response = await axios.post(
      SERVER_URL + NEIGHBORHOOD_DICTIONARY_ENDPOINT + '/add',
      {
        name,
        organization_id: orgId,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const addNeighborhoodMutation = useMutation({
    mutationKey: ['add-neighborhood'],
    mutationFn: addNeighborhood,
    onSuccess: () => {
      setName('');
      refetch && refetch();
    },
    onError: (error: DictType) => {
      toast({
        title: translate('Error'),
        description:
          error?.response?.data?.message ||
          `${translate('Error occurred while updating')} ${translate('neighborhood')}`,
        variant: 'error',
      });
    },
  });

  const onSubmit = () => {
    if (!name.trim()) return;
    addNeighborhoodMutation.mutate();
  };

  return (
    <div
      className={cn('flex items-center gap-2 mt-2', isHebrew ? 'rtl' : 'ltr')}
    >
      <Input
        className="rounded h-8 text-sm"
        value={name}
        placeholder={translate('Neighborhood')}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          }
        }}
      />
      <Button
        onClick={onSubmit}
        type="button"
        size="sm"
        className="rounded-full h-7 w-7 m-0 p-0"
        disabled={addNeighborhoodMutation.isPending}
      >
        <PlusCircle className="h-7 w-7 rounded-full" />
      </Button>
    </div>
  );
};

export default AddNeighborhoodForm;
